import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Copyright } from "../../../components/Copyright";
import { authenticationService } from "../../../services/authentication";
import type { VerifyEmailAddressParameters } from "../../../services/authentication/types";
import { buildSignInPageUrl } from "../../../utils/BuildURLs";
import { SCHOOL_BACKGROUND_IMAGE } from "../../../utils/Constants";

export const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParameters] = useSearchParams();
  const emailAddress = searchParameters.get("emailAddress")!;
  const [verificationCode, setVerificationCode] = useState("");

  const handleVerificationCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setVerificationCode(event.target.value);
  };

  const { mutate: verifyEmailAddress, isLoading: isVerifyingEmailAddress } = useMutation<unknown, Error, VerifyEmailAddressParameters>(
    ({ emailAddress, verificationCode }: VerifyEmailAddressParameters) =>
      authenticationService.verifyEmailAddress({
        emailAddress,
        verificationCode,
      }),
  );

  const handleVerifyEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    verifyEmailAddress(
      { emailAddress, verificationCode },
      {
        onSuccess: () => {
          toast.success(t("authentication.verify_email_page.success_message"));
          const signInPageUrl = buildSignInPageUrl();
          navigate(signInPageUrl);
        },
      },
    );
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: SCHOOL_BACKGROUND_IMAGE,
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundSize: "contain",
          backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5">
            {t("authentication.verify_email_page.verify_email_address")}
          </Typography>
          <Box component="form" onSubmit={handleVerifyEmailSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email-verification"
              label={t("authentication.verify_email_page.verification_code")}
              name="email-verification"
              autoComplete="verification code"
              autoFocus
              onChange={handleVerificationCodeChange}
              disabled={isVerifyingEmailAddress}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={(event) => {
                event.currentTarget?.form?.current?.reportValidity();
              }}
              sx={{ mt: 3, mb: 2 }}
            >
              {t("authentication.verify_email_page.verify_email_address")}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link href={buildSignInPageUrl({ email: emailAddress })} variant="body2">
                  {t("authentication.verify_email_page.sign_in")}
                </Link>
              </Grid>
            </Grid>
            <Copyright sx={{ mt: 5 }} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
